/**
 * 分類マスター 5 型の書込（API §3.2 の `manage_spec_masters`）— 作り直しの形。
 *
 * 旧経路との違いは 3 つである。
 *
 * 1. **対象が増える。** 動詞（`Verb`）と単位（`Unit`）、ステージ（`Stage`）がマスターに入った
 *    （NE §1-subsets）。どれも名前を組み立てる材料か分類そのもので、
 *    増えると同じことをする箱が別の名前を持てるようになる。
 * 2. **操作は `upsert` と `delete` の 2 つになる。** id が不変の番号になったので、改名はラベル
 *    （名前の作り）の更新に畳まれた（決定 18。2026-09-21）。参照の張り替えも要らない。
 * 3. **ラベルを自由文で受けるのは動詞と単位だけになる。** ほかの 3 型は語の組み合わせから
 *    導出する（NE §1-label。欄そのものを持たない）。
 *
 * **誰が書けるかはここでは決めない。** 管理者のロールは transport の認証情報から決まる事実で、
 * コアは知り得ない。ここが持つのは「その操作に管理者のロールが
 * 要るか」の判定（`masterWriteRequiresAdmin`）までで、ロールを引くのは呼び出し側である。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 分類マスター 5 型（動詞・単位・アクター・ドメイン・ステージ）の作り直しの書込。管理者のロールが要るかの判定と、欄の形の宣言を持つ
 * @graph-connects none
 */
import { z } from 'zod';
import { actorElementSchema, domainElementSchema, specElementId, specPjNumberValue, stageElementSchema, unitElementSchema, verbElementSchema, } from '../../contract/elements/index.js';
import { APPROVAL_TARGET_NODE_TYPES, MASTER_NODE_TYPES, } from '../../contract/node-types.js';
import { applyElementWrite, elementRequirementSchema, elementWriteTargetIdSchema, rejectElementWrite, toInputSchemaRejections, } from './write.js';
/**
 * マスターへの操作 2 種。**改名は無い**（決定 18）。
 *
 * id が不変の番号になったので、名前を変えることはラベルの欄を書き直すことになった。今の経路が
 * 持つ「改名 = パスの付け替え + 全参照の追従」は、作り直しでは表せる形そのものが無い。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しのマスターへの操作 2 種（upsert / delete）の閉集合
 * @graph-connects none
 */
export const ELEMENT_MASTER_OPERATIONS = ['upsert', 'delete'];
/** クラスの用語を指す欄。アクター・ドメイン・ステージの名前の材料になる。
 * @graph-connects none */
const CLASS_TERM = { nodeType: 'Term', section: 'class' };
/**
 * マスター 5 型の欄の形（契約の入力スキーマと、欄の中の参照へ至る道）。
 *
 * 動詞と単位は参照を持たない — 組み立てる材料が自分より下に無いためである（NE §1-label）。
 * 残る 3 型はラベルを語から組むので、その語への参照を欄に持つ。
 *
 * ステージの `entry.business` と `exit.business` はここに載せていない。参照先に求める姿
 * （`ExpectedReference`）が用語・動詞・単位しか表せないためで、型の取り違えは書込の関門では
 * 止まらず、リリースゲート（制約 61・70）が見る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business マスター 5 型それぞれの入力スキーマと、欄の中の参照へ至る道の宣言
 * @graph-connects none
 */
export const MASTER_ELEMENT_SPECS = {
    Verb: { schema: verbElementSchema, references: [] },
    Unit: { schema: unitElementSchema, references: [] },
    Actor: {
        schema: actorElementSchema,
        references: [
            { path: ['subject'], expect: CLASS_TERM },
            { path: ['qualifier'], expect: CLASS_TERM },
        ],
    },
    Domain: {
        schema: domainElementSchema,
        references: [
            { path: ['subject'], expect: CLASS_TERM },
        ],
    },
    Stage: {
        schema: stageElementSchema,
        references: [
            { path: ['subject'], expect: CLASS_TERM },
            { path: ['verb'], expect: { nodeType: 'Verb' } },
            { path: ['entry', 'ending'], expect: { nodeType: 'Term', section: 'value' } },
            { path: ['exit', 'ending'], expect: { nodeType: 'Term', section: 'value' } },
        ],
    },
};
/**
 * その操作に管理者のロールが要るかを判定する。
 *
 * 要らないのは **承認の対象（`APPROVAL_TARGET_NODE_TYPES`）のマスターの `upsert` だけ**である。
 * 承認の対象は積んだ変更が `pending` で生まれ、承認が関門になる
 * （制約 31。未承認が残る案件は確定できない）。別の allowlist を立てず承認の対象から導くのは、
 * 承認とロールという 2 つの関門のどちらかが必ず掛かる、という関係を 1 箇所で表すためである。
 *
 * **動詞と単位は全操作が関門の側である**（API §3.1b「対象が増える」）。どちらも承認の対象では
 * なく、書けるのが管理者だけなので承認を関門に置く理由が無い（NE §1-subsets）。
 *
 * **ドメインも承認の対象なので、追加・変更はロールを問わない**（仕様オーナーの決定 2026-09-27
 * 「ドメインは承認を通すので MCP で制限しない」）。書いた変更は他の承認対象と同じく `pending` で
 * 生まれ、承認を経ないと確定できない。
 *
 * 削除は型によらず関門の側に置く。残った参照を宙吊りにする操作で、承認を待つ形にならない。
 *
 * **この判定が意味を持つのは、書込の側が申告された型と既にある要素の実型を突き合わせるから
 * である**（`checkTargetsAndReferences` の `target-type-mismatch`）。突き合わせが無いと、管理者のロールの
 * 要らないステージを名乗って既にある動詞・単位の番号を指すだけでこの関門を抜けられる。
 *
 * @param operation マスターへの操作
 * @param masterType マスターの型
 * @returns 管理者のロールが要るなら true
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business マスターへの操作に管理者のロールが要るかを、操作と型から判定する
 * @graph-connects none
 */
export function masterWriteRequiresAdmin(operation, masterType) {
    if (operation !== 'upsert')
        return true;
    return !APPROVAL_TARGET_NODE_TYPES.some((nodeType) => nodeType === masterType);
}
/**
 * マスターの書込の入力。**1 件ずつ**である（API §3.1 の「1 件ずつのツール」）。
 *
 * マスターは 1 件で完結する経路なので、配列にすると意味のない 1 要素の配列を組むことになる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しのマスターの書込 1 件の入力スキーマ（upsert / delete の判別ユニオン）
 * @graph-connects none
 */
export const masterElementWriteInputSchema = z.discriminatedUnion('operation', [
    z.strictObject({
        operation: z.literal('upsert'),
        pjNumber: specPjNumberValue(),
        dryRun: z.boolean(),
        masterType: z.enum(MASTER_NODE_TYPES),
        requirement: elementRequirementSchema,
        /** 省略 = 新規。既にあるマスターを直すなら正の番号 */
        id: elementWriteTargetIdSchema.optional(),
        /** マスター 1 件の欄。型ごとの入力スキーマに通す */
        element: z.unknown(),
    }),
    z.strictObject({
        operation: z.literal('delete'),
        pjNumber: specPjNumberValue(),
        dryRun: z.boolean(),
        masterType: z.enum(MASTER_NODE_TYPES),
        requirement: elementRequirementSchema,
        /** 消す対象の番号 */
        id: specElementId(),
    }),
]);
/**
 * マスターを 1 件書く（追加・変更・削除）。
 *
 * ロールの判定は呼び出し側で済ませてから呼ぶ（`masterWriteRequiresAdmin`）。ここにロールの判定を持ち
 * 込むと「呼び出し元が自称した管理者フラグ」を信じる形にしかならない。
 *
 * @param raw 入力（形式検査の前）
 * @param actor 認証済みの書込主体
 * @param deps 置き場・採番・master の読取・適用時刻
 * @returns 適用 / 検査だけ / 拒否
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business マスター 1 件の追加・変更・削除を、作り直しの書込の道（採番 → 検査 → 2 表への書き分け）に通す
 * @graph-connects applyElementWrite [calls] 書込 1 回分の適用
 */
export async function writeSpecMasterElement(raw, actor, deps) {
    const parsed = masterElementWriteInputSchema.safeParse(raw);
    if (!parsed.success) {
        return { outcome: 'rejected', rejections: toInputSchemaRejections('', parsed.error) };
    }
    const input = parsed.data;
    if (input.operation === 'upsert' && input.id !== undefined && input.id < 0) {
        return rejectElementWrite('input-schema', 'id', 'マスターは 1 件ずつ書くので、同じ呼び出しの中で作るものを指す仮の番号は使えない（新規は id を省く）');
    }
    return applyElementWrite({
        pjNumber: input.pjNumber,
        dryRun: input.dryRun,
        actor,
        writeKind: 'write',
        items: [
            input.operation === 'delete'
                ? {
                    operation: 'delete',
                    nodeType: input.masterType,
                    requirementId: input.requirement.requirementId,
                    id: input.id,
                }
                : {
                    operation: 'upsert',
                    nodeType: input.masterType,
                    requirementId: input.requirement.requirementId,
                    id: input.id,
                    payload: input.element,
                    spec: MASTER_ELEMENT_SPECS[input.masterType],
                },
        ],
        deps,
    });
}
//# sourceMappingURL=masters.js.map